import {useState} from "react";
import {useEndpointContext} from "@/components/providers/EndpointProvider";
import {flattenBodySchema} from "@/pages/scenario/utils/bodySchemaUtils";

const epTag = (ep) => ep.tag.startsWith('#') ? ep.tag : `#${ep.tag}`

const useEndpointVariables = (initialDynamicValues = []) => {
    const { endpointObjects, isLoading } = useEndpointContext();
    const endpointVariables = endpointObjects.map(e => ({id: e.id, display: e.tag.replace(/^#/, ''), provider: e.provider}));
    const [dynamicValues, setDynamicValues] = useState(
        initialDynamicValues.map(dv => ({
            ...dv,
            params: (dv.params ?? []).map(p =>
                'name' in p && 'value' in p && Object.keys(p).length === 2
                    ? {[p.name]: p.value}
                    : p
            ),
            bodySchema: dv.bodySchema ?? dv.bodyValues ?? {}
        }))
    )
    const [paramModalOpen, setParamModalOpen] = useState(false)
    const [selectedEndpoint, setSelectedEndpoint] = useState(null)

    const handleMentionClick = (varId) => {
        const ep = endpointObjects.find(e => e.tag === varId || e.tag === `#${varId}`)
        if (ep) {
            setSelectedEndpoint(ep)
            setParamModalOpen(true)
        }
    }

    const handleParamValuesSave = (endpointId, paramValues, bodyValues) => {
        const ep = endpointObjects.find(e => e.id === endpointId)
        if (!ep) return
        const tag = epTag(ep)
        const params = Object.entries(paramValues).map(([k, v]) => ({[k]: v}))
        setDynamicValues(prev => {
            const exists = prev.some(v => v.name === tag)
            if (exists) {
                return prev.map(v => v.name === tag ? {...v, params, bodySchema: bodyValues} : v)
            }
            return [...prev, {name: tag, endpointId: ep.id, endpointMethod: ep.method, endpointUrl: ep.url, params, bodySchema: bodyValues}]
        })
    }

    const handleMentionDelete = (varId) => {
        const tag = varId.startsWith('#') ? varId : `#${varId}`
        setDynamicValues(prev => prev.filter(v => v.name !== tag))
    }

    const handleVariableSelect = (varId) => {
        const ep = endpointObjects.find(e => e.tag === varId || e.tag === `#${varId}`)
        if (!ep) return
        const tag = epTag(ep)
        setDynamicValues(prev => {
            if (prev.some(v => v.name === tag)) return prev
            const params = ep.params?.map(p => ({[p.name]: p.defaultValue ?? ""})) ?? []
            const bodySchema = Object.fromEntries(flattenBodySchema(ep.bodySchema ?? []).map(f => [f.path, f.defaultValue ?? ""]))
            return [...prev, {name: tag, endpointId: ep.id, endpointMethod: ep.method, endpointUrl: ep.url, params, bodySchema}]
        })
    }

    const closeParamModal = () => setParamModalOpen(false)

    const getInitialParamValues = () => {
        if (!selectedEndpoint) return {}
        const entry = dynamicValues.find(v => v.name === epTag(selectedEndpoint))
        if (!entry) return {}
        return Object.fromEntries(entry.params.map(p => Object.entries(p)[0]))
    }

    const getInitialBodySchemaValues = () => {
        if (!selectedEndpoint) return {}
        const entry = dynamicValues.find(v => v.name === epTag(selectedEndpoint))
        return entry?.bodySchema ?? {}
    }

    return {
        endpointObjects,
        endpointVariables,
        dynamicValues,
        isLoading,
        paramModalOpen,
        selectedEndpoint,
        handleMentionClick,
        handleMentionDelete,
        handleVariableSelect,
        handleParamValuesSave,
        closeParamModal,
        getInitialParamValues,
        getInitialBodySchemaValues,
    }
}

export default useEndpointVariables;
